import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../Icon';
import EmptyState from '../EmptyState';
import Button from '../ui/Button';
import ConfirmDialog from '../ui/ConfirmDialog';
import LoadErrorBox from '../ui/LoadErrorBox';
import { useNotifications } from '../NotificationProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../hooks/useRole';
import { getActiveProjectId } from '../../utils/session';
import { errorMessageOf } from '../../utils/apiErrors';
import { ROLE_LABELS } from '../../utils/roles';
import { matchesPerson } from '../../utils/accountSearch';
import { useHeaderSearch } from '../../hooks/useHeaderSearch';
import AddPersonForm from './AddPersonForm';
import PersonRow from './PersonRow';
import { usePeople } from './usePeople';
import { useExport } from '../header/exporters';
import { filterPeople, knownTeams, workspaceProjects, type PeopleFilter } from './peopleView';
import type { Person, PersonProject } from '../../types/People';
import '../../styles/components/People.css';

interface RemoveTarget { person: Person; project: PersonProject }

/**
 * Team and contacts: everyone in the workspace in one list, with their login
 * and the projects whose team they are on. A login's role applies to the whole
 * workspace; being on a project's team does not change who can see which
 * project. Searching uses the page header's search box.
 */
interface PeopleDirectoryProps {
  /** Which people to list. The page owns it (the Archived switch and the Login status filter live in its header bar). */
  filter?: PeopleFilter;
  onChanged?: () => void;
  /**
   * When the page owns the Add person button (the Users page puts it in the header bar), it passes a counter
   * and raises it on each click; the form opens whenever the counter goes up, and the card drops its own button.
   */
  openAddSignal?: number;
}

const PeopleDirectory: React.FC<PeopleDirectoryProps> = ({ filter = 'all', onChanged, openAddSignal }) => {
  const { workspaces } = useAuth();
  const { isAdmin, isOwner } = useRole();
  const { showError, showSuccess } = useNotifications();
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<Person | null>(null);

  const data = usePeople(filter === 'archived' ? 'archived' : 'active');
  const projects = useMemo(() => workspaceProjects(workspaces, getActiveProjectId()), [workspaces]);
  // The header search narrows the list.
  const search = useHeaderSearch();
  const visible = useMemo(
    () => filterPeople(data.people, filter).filter(p => matchesPerson(p, search)),
    [data.people, filter, search],
  );
  const teams = useMemo(() => knownTeams(data.people), [data.people]);

  // The page's Export button writes the people listed right now.
  useExport('account-people', { rows: visible, ctx: undefined });

  const lastSignal = useRef(openAddSignal ?? 0);
  useEffect(() => {
    if (openAddSignal !== undefined && openAddSignal > lastSignal.current) setAdding(true);
    lastSignal.current = openAddSignal ?? 0;
  }, [openAddSignal]);

  const done = (message: string) => { showSuccess(message); onChanged?.(); };

  const quick = async (person: Person, change: () => Promise<void>, message: string) => {
    setBusyId(person.id);
    try {
      await change();
      done(message);
    } catch (err) {
      showError(errorMessageOf(err, 'That did not go through. Please try again.'));
    } finally {
      setBusyId(null);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await data.removeFromProject(removeTarget.person.id, removeTarget.project.project_id);
      done(`${removeTarget.person.name} taken off ${removeTarget.project.project_name}`);
      setRemoveTarget(null);
    } catch (err) {
      setRemoveError(errorMessageOf(err, "Couldn't remove this person from the project. Please try again."));
    } finally {
      setRemoving(false);
    }
  };

  const emptyTitle = search.trim() ? 'No one matches your search' : filter === 'archived' ? 'No archived people' : filter === 'all' ? 'No people yet' : 'No one matches this filter';

  return (
    <div className="settings-card people-directory">
      <div className="people-directory__head">
        <div>
          <h4 className="settings-card-title">Team and contacts</h4>
          <p className="settings-muted-xs">
            Everyone in this workspace in one list: people who sign in, people on a project team, and contacts who
            never sign in. A login&apos;s role is its default for the whole workspace; a project can set its own.
          </p>
        </div>
        {isAdmin && !adding && openAddSignal === undefined && (
          <Button variant="primary" size="sm" icon={<Icon name="plus" size={14} />} onClick={() => setAdding(true)}>Add person</Button>
        )}
      </div>

      {adding && (
        <AddPersonForm
          onCancel={() => setAdding(false)}
          onSave={async input => { await data.add(input); done(`${input.name.trim()} added`); }}
        />
      )}

      {data.loadError != null ? (
        <>
          <LoadErrorBox what="the people" error={data.loadError} onRetry={() => void data.refresh()} retrying={data.loading} />
          <p className="settings-muted-xs">
            The people directory may not be set up on the server yet. Retry will not help until it is.
          </p>
        </>
      ) : data.loading && data.people.length === 0 ? (
        <p className="team-loading" role="status">Loading people…</p>
      ) : visible.length === 0 ? (
        <EmptyState
          compact
          icon="users"
          title={emptyTitle}
          description={filter === 'all' && isAdmin && !search.trim() ? 'Add the first person with the button above.' : undefined}
        />
      ) : (
        <div className="people-list">
          {visible.map(person => (
            <PersonRow
              key={person.id}
              person={person}
              canEdit={isAdmin}
              canGiveLogin={isOwner}
              projects={projects}
              knownTeams={teams}
              busy={busyId === person.id}
              onCreateLogin={async (role, email) => {
                const made = await data.createLogin(person.id, role, email);
                const kept = made?.login?.role;
                done(kept && kept !== role
                  ? `Login linked for ${person.name}. A login for this e-mail already existed, so it keeps its role: ${ROLE_LABELS[kept] ?? kept}.`
                  : `Login created for ${person.name}`);
              }}
              onAddToProject={async (projectId, projectTeams) => { await data.addToProject(person.id, projectId, projectTeams); done(`${person.name} added to the project`); }}
              onRemoveFromProject={project => { setRemoveError(null); setRemoveTarget({ person, project }); }}
              onArchive={() => {
                if (person.login) setArchiveTarget(person);
                else void quick(person, () => data.archive(person.id), `${person.name} archived`);
              }}
              onRestore={() => void quick(person, () => data.restore(person.id), `${person.name} restored`)}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={archiveTarget !== null}
        onClose={() => setArchiveTarget(null)}
        onConfirm={() => {
          const target = archiveTarget!;
          setArchiveTarget(null);
          void quick(target, () => data.archive(target.id), `${target.name} archived`);
        }}
        title="Archive this person?"
        description={archiveTarget ? `${archiveTarget.name} moves to Archived. Their login is not changed. They can still sign in until you deactivate it in Logins.` : undefined}
        confirmLabel="Archive"
        variant="primary"
      />

      <ConfirmDialog
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => void confirmRemove()}
        title="Take off this project?"
        description={removeTarget
          ? `${removeTarget.person.name} will no longer be on the ${removeTarget.project.project_name} team. Their login and their other projects are not changed. The team entry goes to that project's trash, where it can be restored.`
          : undefined}
        confirmLabel="Remove"
        error={removeError}
        loading={removing}
      />
    </div>
  );
};

export default PeopleDirectory;
